import { useEffect, useMemo, useState } from "react";
import { useApi } from "@/hooks/useApi";
import {
  deleteDownload,
  getDiscoveryHome,
} from "@/api/endpoints";
import { formatByteSize } from "@/lib/byteSize";
import { ScrollRow } from "@/components/ScrollRow";
import { MediaCard, ContinueCard } from "@/components/MediaCard";
import type { DiscoverySection, Download, Item, ResumeItem, WatchStateResource } from "@/types/api";
import {
  buildResumeCardProgressMap,
  buildWatchStateResourceMap,
  cardProgressForItem,
  type CardWatchProgress,
} from "@/lib/watchState";
import {
  clearItemPlaybackState,
  markItemPlayed,
} from "@/lib/watchPlayback";

const HOME_ROW_LIMIT = 20;
// While the page stays visible, Home also refreshes on this period so newly
// indexed episodes (knotfin#1014) appear without a reload or refocus.
const HOME_REFRESH_INTERVAL_MS = 60_000;
const EMPTY_HOME_SECTIONS: DiscoverySection[] = [];
const EMPTY_WATCH_STATES: WatchStateResource[] = [];
const EMPTY_ITEMS: Item[] = [];
const EMPTY_RESUME_ITEMS: ResumeItem[] = [];
const EMPTY_DOWNLOADS: Download[] = [];

export function HomePage() {
  const [watchError, setWatchError] = useState<string | null>(null);
  const [watchUpdatingId, setWatchUpdatingId] = useState<string | null>(null);
  const [downloadDeletingId, setDownloadDeletingId] = useState<string | null>(null);
  const [watchOverrides, setWatchOverrides] = useState<Record<string, boolean>>({});
  const home = useApi(() => getDiscoveryHome({ limit: HOME_ROW_LIMIT }), []);
  const homeSections = home.data?.data ?? EMPTY_HOME_SECTIONS;
  const watchStates = home.data?.meta.watch_states ?? EMPTY_WATCH_STATES;
  const watchStateByItem = useMemo(
    () => buildWatchStateResourceMap(watchStates),
    [watchStates],
  );
  const continueWatching = sectionResumeItems(homeSections, "continue_watching");
  const resumeProgressByKey = useMemo(
    () => buildResumeCardProgressMap(continueWatching),
    [continueWatching],
  );

  useEffect(() => {
    setWatchOverrides({});
  }, [watchStates]);

  // Refresh Continue Watching progress when this window regains focus, so its
  // bars match playback happening in another window/device, and once a minute
  // while visible so new episodes show up. No persistent connection is held. The
  // throttle absorbs the focus + visibilitychange double-fire and rapid tab
  // toggling.
  const refreshHome = home.refetch;
  useEffect(() => {
    let lastRefreshAt = 0;
    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastRefreshAt < 3000) {
        return;
      }
      lastRefreshAt = now;
      void refreshHome().catch(() => {});
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    window.addEventListener("focus", refreshIfVisible);
    // Hidden tabs skip the periodic refresh (refreshIfVisible checks), and a
    // refresh keeps the current cards on screen until the new data arrives.
    const interval = window.setInterval(refreshIfVisible, HOME_REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      window.removeEventListener("focus", refreshIfVisible);
      window.clearInterval(interval);
    };
  }, [refreshHome]);

  function isItemWatched(item: Item): boolean {
    if (item.id in watchOverrides) {
      return watchOverrides[item.id]!;
    }
    return watchStateByItem.get(item.id)?.attributes.watched === true;
  }

  async function handleToggleWatched(item: Item) {
    setWatchUpdatingId(item.id);
    setWatchError(null);
    try {
      const isWatched = isItemWatched(item);
      if (isWatched) {
        await clearItemPlaybackState(item.id);
        setWatchOverrides((current) => ({ ...current, [item.id]: false }));
      } else {
        await markItemPlayed(item.id);
        setWatchOverrides((current) => ({ ...current, [item.id]: true }));
      }
      void home.refetch().catch(() => {});
    } catch {
      setWatchError("Failed to update watched state.");
    } finally {
      setWatchUpdatingId(null);
    }
  }

  async function handleRemoveFromContinueWatching(itemId: string) {
    setWatchUpdatingId(itemId);
    setWatchError(null);
    try {
      await markItemPlayed(itemId);
      setWatchOverrides((current) => ({ ...current, [itemId]: true }));
      await home.refetch();
    } catch {
      setWatchError("Failed to remove item from Continue Watching.");
    } finally {
      setWatchUpdatingId(null);
    }
  }

  async function handleDeleteDownload(download: Download) {
    setDownloadDeletingId(download.id);
    setWatchError(null);
    try {
      await deleteDownload(download.id);
      await home.refetch();
    } catch {
      setWatchError("Failed to remove download.");
    } finally {
      setDownloadDeletingId(null);
    }
  }

  const watchlistItems = sectionItems(homeSections, "watchlist");
  const recentDownloads = sectionDownloads(homeSections, "offline_downloads");
  const recentReleasedEpisodes = sectionItems(homeSections, "recently_released_episodes");
  const recentReleasedMovies = sectionItems(homeSections, "recently_released_movies");
  const recentEpisodes = sectionItems(homeSections, "recently_added_episodes");
  const recentMovies = sectionItems(homeSections, "recently_added_movies");

  return (
    <div className="home-page">
      {watchError && <p className="error-message">{watchError}</p>}
      {/* Continue Watching */}
      {continueWatching.length > 0 && (
        <ScrollRow title="Continue Watching">
          {continueWatching.map((resumeItem) => (
            <ContinueCard
              key={resumeItem.id}
              resumeItem={resumeItem}
              removeUpdating={watchUpdatingId === resumeItem.relationships.item.data.id}
              onRemoveFromContinueWatching={(itemId) => void handleRemoveFromContinueWatching(itemId)}
            />
          ))}
        </ScrollRow>
      )}

      <HomeItemRow
        title="Watchlist"
        items={watchlistItems}
        progressByItem={watchStateByItem}
        resumeProgressByKey={resumeProgressByKey}
        watchOverrides={watchOverrides}
        watchUpdatingId={watchUpdatingId}
        onToggleWatched={handleToggleWatched}
      />

      {recentDownloads.length > 0 && (
        <OfflineDownloadsPanel
          downloads={recentDownloads.slice(0, 5)}
          deletingId={downloadDeletingId}
          onDelete={handleDeleteDownload}
        />
      )}

      <HomeItemRow
        title="Recently Released Episodes"
        shape="backdrop"
        viewAllLink={viewAllLinkForItems(recentReleasedEpisodes, "sort=-release_date&item_type=episode")}
        items={recentReleasedEpisodes}
        progressByItem={watchStateByItem}
        resumeProgressByKey={resumeProgressByKey}
        watchOverrides={watchOverrides}
        watchUpdatingId={watchUpdatingId}
        onToggleWatched={handleToggleWatched}
      />

      <HomeItemRow
        title="Recently Released Movies"
        viewAllLink={viewAllLinkForItems(recentReleasedMovies, "sort=-release_date")}
        items={recentReleasedMovies}
        progressByItem={watchStateByItem}
        resumeProgressByKey={resumeProgressByKey}
        watchOverrides={watchOverrides}
        watchUpdatingId={watchUpdatingId}
        onToggleWatched={handleToggleWatched}
      />

      <HomeItemRow
        title="Recently Added Episodes"
        shape="backdrop"
        viewAllLink={viewAllLinkForItems(recentEpisodes, "sort=-created_at&item_type=episode")}
        items={recentEpisodes}
        progressByItem={watchStateByItem}
        resumeProgressByKey={resumeProgressByKey}
        watchOverrides={watchOverrides}
        watchUpdatingId={watchUpdatingId}
        onToggleWatched={handleToggleWatched}
      />

      <HomeItemRow
        title="Recently Added Movies"
        viewAllLink={viewAllLinkForItems(recentMovies, "sort=-created_at")}
        items={recentMovies}
        progressByItem={watchStateByItem}
        resumeProgressByKey={resumeProgressByKey}
        watchOverrides={watchOverrides}
        watchUpdatingId={watchUpdatingId}
        onToggleWatched={handleToggleWatched}
      />
    </div>
  );
}

function OfflineDownloadsPanel({
  downloads,
  deletingId,
  onDelete,
}: {
  downloads: Download[];
  deletingId: string | null;
  onDelete: (download: Download) => void;
}) {
  return (
    <section className="home-downloads">
      <div className="home-downloads-heading">
        <div>
          <h2>Offline Downloads</h2>
          <p>Prepared files are served from this server until they expire or are removed.</p>
        </div>
      </div>
      <div className="home-download-list">
        {downloads.map((download) => (
          <div key={download.id} className="home-download-row">
            <a
              className="home-download-title"
              href={download.attributes.download_path}
              download
              aria-disabled={download.attributes.state === "expired"}
              onClick={(event) => {
                if (download.attributes.state === "expired") {
                  event.preventDefault();
                }
              }}
            >
              {download.attributes.item_title || download.relationships.item.data.id}
            </a>
            <span className={`home-download-state home-download-state--${download.attributes.state}`}>
              {download.attributes.state}
            </span>
            <span className="home-download-meta">
              {formatByteSize(download.attributes.size_bytes)}
              {download.attributes.expires_at ? ` · expires ${formatDownloadDate(download.attributes.expires_at)}` : ""}
            </span>
            <button
              className="btn btn--ghost home-download-delete"
              type="button"
              onClick={() => onDelete(download)}
              disabled={deletingId === download.id}
            >
              {deletingId === download.id ? "Removing..." : "Remove"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function formatDownloadDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function HomeItemRow({
  title,
  shape,
  viewAllLink,
  items,
  progressByItem,
  resumeProgressByKey,
  watchOverrides,
  watchUpdatingId,
  onToggleWatched,
}: {
  title: string;
  shape?: "portrait" | "backdrop" | "square";
  viewAllLink?: string;
  items: Item[];
  progressByItem: Map<string, WatchStateResource>;
  resumeProgressByKey: Map<string, CardWatchProgress>;
  watchOverrides: Record<string, boolean>;
  watchUpdatingId: string | null;
  onToggleWatched: (item: Item) => void;
}) {
  if (items.length === 0) return null;

  return (
    <ScrollRow
      title={title}
      viewAllLink={viewAllLink}
      expandable
      expandedMinCardWidth={shape === "backdrop" ? "260px" : "170px"}
    >
      {items.map((item) => {
        const cardProgress = cardProgressForItem(item, progressByItem, resumeProgressByKey);
        return (
          <MediaCard
            key={item.id}
            item={item}
            shape={shape}
            progress={cardProgress?.progress}
            progressLabel={cardProgress?.label}
            progressTitle={cardProgress?.title}
            watched={item.id in watchOverrides
              ? watchOverrides[item.id]
              : progressByItem.get(item.id)?.attributes.watched === true}
            watchUpdating={watchUpdatingId === item.id}
            onToggleWatched={() => void onToggleWatched(item)}
          />
        );
      })}
    </ScrollRow>
  );
}


function viewAllLinkForItems(items: Item[], query: string): string | undefined {
  const libraryId = items[0]?.relationships.library.data.id;
  if (!libraryId) {
    return undefined;
  }
  return `/libraries/${libraryId}?${query}`;
}

function sectionById(sections: DiscoverySection[], id: string): DiscoverySection | undefined {
  return sections.find((section) => section.id === id);
}

function sectionItems(sections: DiscoverySection[], id: string): Item[] {
  return sectionById(sections, id)?.attributes.items ?? EMPTY_ITEMS;
}

function sectionResumeItems(sections: DiscoverySection[], id: string): ResumeItem[] {
  return sectionById(sections, id)?.attributes.resume_items ?? EMPTY_RESUME_ITEMS;
}

function sectionDownloads(sections: DiscoverySection[], id: string): Download[] {
  return sectionById(sections, id)?.attributes.downloads ?? EMPTY_DOWNLOADS;
}
